import { CameraControls } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useRef } from "react";

import * as THREE from "three";

type CameraSetup = {
  position: THREE.Vector3;
  target: THREE.Vector3;
  rotation: THREE.Euler;
  near: number;
  far: number;
  fov?: number;
  movementSpeed: number;
  panSpeed: number;
  orbitMinDistance: number;
  orbitMaxDistance: number;
  orbitInitialDistance: number;
};

const tempTarget = new THREE.Vector3(0, 0, 0);

export const cameraSetups: { [key: string]: CameraSetup } = {
  enclosure: {
    position: new THREE.Vector3(
      4.962727392066462,
      9.065193636090791,
      12.3868554101691,
    ),
    target: new THREE.Vector3(
      -5.654397734784007,
      -1.4340526117696366,
      21.9788071564226,
    ),
    rotation: new THREE.Euler(-2.1, 0.65, 2.3),
    near: 0.005,
    far: 1000,
    fov: 50,
    movementSpeed: 10,
    panSpeed: 10,
    orbitMinDistance: 5,
    orbitMaxDistance: 20,
    orbitInitialDistance: 10,
  },
  system: {
    position: new THREE.Vector3(
      -5.383190285532632,
      15.029014146122169,
      25.51566992286602,
    ),
    target: new THREE.Vector3(
      -5.383190245614099,
      -7.537052611590821,
      25.51565235684462,
    ),
    rotation: new THREE.Euler(-1.57, 0, 0.01),
    near: 0.005,
    far: 1000,
    movementSpeed: 3,
    panSpeed: 1,
    orbitMinDistance: 5,
    orbitMaxDistance: 20,
    orbitInitialDistance: 10,
  },
  chipDie: {
    position: new THREE.Vector3(
      -10.266831357225492,
      1.8375107087571345,
      25.130470326795283,
    ),
    target: new THREE.Vector3(
      -10.078821808719745,
      1.665054992951028,
      25.418357292410516,
    ),
    rotation: new THREE.Euler(-2.47, -0.385, -2.852),
    near: 0.005,
    far: 1000,
    fov: 50,
    movementSpeed: 1,
    panSpeed: 0.01,
    orbitMinDistance: 0.1,
    orbitMaxDistance: 2,
    orbitInitialDistance: 1,
  },

  // TODO
  insideChip: {
    position: new THREE.Vector3(
      -10.160171384332944,
      1.697251960217636,
      25.268130561985505,
    ),
    target: new THREE.Vector3(
      -10.067715095637997,
      1.6026099186952962,
      25.350706826627146,
    ),
    rotation: new THREE.Euler(-2.47, -0.385, -2.852),
    near: 0.005,
    far: 1000,
    fov: 50,
    movementSpeed: 0.05,
    panSpeed: 0.005,
    orbitMinDistance: 0.1,
    orbitMaxDistance: 2,
    orbitInitialDistance: 1,
  },

  fuseBreak: {
    position: new THREE.Vector3(
      -10.135054133778258,
      1.6946882710668898,
      25.287068002904252,
    ),
    target: new THREE.Vector3(
      -10.132668449395764,
      1.6795733603911263,
      25.291323700949654,
    ),
    rotation: new THREE.Euler(-2.47, -0.385, -2.852),
    near: 0.005,
    far: 1000,
    fov: 50,
    movementSpeed: 0.05,
    panSpeed: 0.005,
    orbitMinDistance: 0.1,
    orbitMaxDistance: 2,
    orbitInitialDistance: 1,
  },
};

function CameraController({
  currentCamera,
  cameraControls,
}: {
  currentCamera: string;
  cameraControls: React.RefObject<CameraControls | undefined>;
}) {
  const {
    camera,
    gl,
    scene,
  }: { camera: THREE.PerspectiveCamera; gl: any; scene: any } = useThree();
  const raycaster = useRef(new THREE.Raycaster());
  const mouse = useRef(new THREE.Vector2());

  useEffect(() => {
    if (cameraSetups[currentCamera]) {
      const setup = cameraSetups[currentCamera];
      cameraControls?.current?.setLookAt(
        setup.position.x,
        setup.position.y,
        setup.position.z,
        setup.target.x,
        setup.target.y,
        setup.target.z,
        true,
      );

      camera.near = setup.near;
      camera.far = setup.far;
      camera.fov = setup.fov || 50;
      camera.updateProjectionMatrix();
    }
  }, [currentCamera]);

  // The idea is that if we right click on a spot, we set the orbit controls target to pivot around that spot.
  // Not working yet
  const handleRightClick = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();

      mouse.current.x = (event.clientX / gl.domElement.clientWidth) * 2 - 1;
      mouse.current.y = -(event.clientY / gl.domElement.clientHeight) * 2 + 1;

      raycaster.current.setFromCamera(mouse.current, camera);

      const intersects = raycaster.current.intersectObjects(
        scene.children,
        true,
      );

      if (intersects.length > 0) {
        const newTarget = intersects[0].point;
        if (cameraControls.current) {
          cameraControls.current.setTarget(
            newTarget.x,
            newTarget.y,
            newTarget.z,
          );
        }
      }
    },
    [
      camera,
      gl.domElement.clientWidth,
      gl.domElement.clientHeight,
      cameraControls,
      scene.children,
    ],
  );

  useEffect(() => {
    // gl.domElement.addEventListener("contextmenu", handleRightClick);
    return () => {
      // gl.domElement.removeEventListener("contextmenu", handleRightClick);
    };
  }, [gl.domElement, handleRightClick]);

  // Add an effect to log camera values on every frame
  useFrame(() => {
    // console.log("Camera Position:", camera.position.toArray());
    // cameraControls?.current?.getTarget(tempTarget);
    // console.log("Camera target: ", tempTarget.toArray());
    // console.log("Camera Rotation:", camera.rotation.toArray());
    // console.log("Camera scale:", camera.scale);
    // console.log("Camera view:", camera.view);
    // console.log("Camera Near:", camera.near);
    // console.log("Camera Far:", camera.far);
    // console.log("Camera Zoom:", camera.zoom);
  });

  return null;
}

export default CameraController;
